import { useBottomPanel } from '@ubnt/link-core-ui/components/BottomPanel/context/BottomPanelContext';
import { useDevicePanel } from '@ubnt/link-core-ui/components/BottomPanel/context/DevicePanelContext';
import DevicePickerDropdown, {
  DevicePickerDropdownProps,
} from '@ubnt/link-core-ui/components/BottomPanel/elements/DevicePickerDropdown';
import DevicePickerVariantsEnum from '@ubnt/link-core-ui/components/DevicePicker/DevicePickerVariantsEnum';
import { RadioApTypesEnum } from '@ubnt/link-core/enums';
import { useMemo } from 'react';

import { useSelector } from 'app/store';
import {
  devicesDeviceRoleSelector,
  devicesDeviceInstanceSelector,
  devicesCustomClientDeviceInstancesSelector,
  devicesDeviceVisibleHiddenByCountrySelector,
  devicesDeviceDefaultInstanceSelector,
} from 'modules/devices/slice';
import { MESH_AP_ID } from 'modules/devices/constants';
import { useApplicationStore } from 'modules/application/store';
import { useDevicePicker } from 'modules/bottomPanel/hooks/useDevicePicker';
import { meshDisabledItemFn } from 'modules/bottomPanel/utils';
import { isRoleAp } from 'modules/devices/utils';

const DevicePickerDropdownContainer = (props: Partial<DevicePickerDropdownProps>) => {
  const { id } = useDevicePanel();
  const { leftId, rightId } = useBottomPanel();
  const { onDeviceChange, actions, itemNotesFn, priceList, hidePrices } = useDevicePicker();

  const apId = id === rightId ? leftId : undefined;

  const apDevice = useSelector((state) => (apId ? devicesDeviceInstanceSelector(state, apId) : undefined));
  const deviceDisplayName = useSelector((state) => id && devicesDeviceDefaultInstanceSelector(state, id).displayName);
  const deviceName = useSelector((state) => (id ? devicesDeviceDefaultInstanceSelector(state, id).name : undefined));
  const role = useSelector((state) => (id ? devicesDeviceRoleSelector(state, id) : null));
  const externalDevices = useSelector(devicesCustomClientDeviceInstancesSelector);
  const visibleHiddenDevices = useSelector(devicesDeviceVisibleHiddenByCountrySelector);
  const apType = RadioApTypesEnum.Ptmp;
  const isMesh = id === MESH_AP_ID;
  const isAp = isRoleAp(role);
  const label = isMesh ? 'Mesh Point' : isAp ? 'Access Point' : 'Client';
  const isMobile = useApplicationStore((state) => state.isMobile);

  const deviceNameTooltipProps = useMemo(() => ({ disableHover: isMobile }), [isMobile]);

  if (!id || !deviceDisplayName || !role) {
    return null;
  }

  return (
    <div data-testid={`device-picker-${id}`}>
      <DevicePickerDropdown
        actions={actions}
        apDevice={apDevice}
        apType={apType}
        label={label}
        deviceName={deviceDisplayName}
        isMesh={isMesh}
        onDeviceChange={onDeviceChange}
        role={role}
        variant={DevicePickerVariantsEnum.WifiIncludes}
        externalDevices={externalDevices}
        selectedDeviceName={deviceName}
        deviceNameTooltipProps={deviceNameTooltipProps}
        visibleHiddenDevices={visibleHiddenDevices}
        disabledItemFn={isMesh ? meshDisabledItemFn : undefined}
        itemNotesFn={itemNotesFn}
        priceList={priceList}
        hidePrices={hidePrices}
        {...props}
      />
    </div>
  );
};

export default DevicePickerDropdownContainer;
